import "./App.css";
import Loader from "./components/common/Loader.jsx";

//Other Imports
import { Routes, Route, BrowserRouter, Navigate, Outlet, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useState } from "react";

//Cookie & Context
import { apiCallAdmin } from "./components/service/api-service";
import { useGlobal } from "./GlobalContext";
import { useUI } from "./UIContext";

//Dashboard
import { Dashboard } from "./components/dashboard/Dashboard.jsx";
import { RevenueDashboard } from "./components/dashboard/revenue-dashboard.js";
import { SalespersonDashboard } from "./components/dashboard/salesperson-dashboard.js";
import { PipelineDashboard } from "./components/dashboard/pipeline-dashboard.js";
import { ForecastDashboard } from "./components/dashboard/forecast-dashboard.js";
import { NextActionDashboard } from "./components/dashboard/next-action-dashboard.js";

//SideBar & Topbar
import Sidebar from "./components/common/sidebar";
import AiChat from "./components/common/ai-chat.js";
import AiUsage from "./components/common/ai-usage.js";
import { askCrmChatApi, getCrmAiProvidersApi } from "./components/common/ai-chat-api.js";
import Topbar from "./components/common/topbar";
import V1MigrationModal from "./components/common/v1-migration-modal/V1MigrationModal.jsx";

//Leads
import LeadsList from "./components/leads/leads-list";
import LeadsAdd from "./components/leads/leads-create";
import LeadsView from "./components/leads/leads-view";
import ContactsCreate from "./components/contacts/contacts-create";
import ContactsList from "./components/contacts/contacts-list";
import ContactsView from "./components/contacts/contacts-view";
import AccountCreate from "./components/accounts/account-create";
import AccountView from "./components/accounts/account-view";
import AccountList from "./components/accounts/account-list";
import OutstandingSchedulesList from "./components/accounts/outstanding-schedules-list";

//Item
import OpportunityList from "./components/opportunity/opportunity-list";
import OpportunityCreate from "./components/opportunity/opportunity-create";
import OpportunityView from "./components/opportunity/opportunity-view";
import ItemList from "./components/items/item-list";
import ItemCreate from "./components/items/item-create";
import ItemView from "./components/items/item-view";

//Calls
import CallsList from "./components/calls/calls-list";
import CallsAdd from "./components/calls/calls-create";
import CalendarView from "./components/calendar/Calendar-View";
import CallsView from "./components/calls/calls-view";
import CallReminderNotifier from "./components/calls/call-reminder-notifier";

//Announcements
import AnnouncementList from "./components/announcement/announcementlist";
import AnnouncementAdd from "./components/announcement/announcementcreate";

//Report
import ReportPage from "./components/report/report";
import QuotationList from "./components/quotation/quotation-list.jsx";
import QuotationCreate from "./components/quotation/quotation-create.jsx";
import QuotationView from "./components/quotation/quotation-view.jsx";

//Payment
import PaymentListing from "./components/payment/payment-list";
import PaymentCreate from "./components/payment/payment-create";
import PaymentView from "./components/payment/payment-view";
import PaymentHistoryView from "./components/payment/payment-history-view";

//Help
import HelpQuestions from "./components/common/help";

//Unauthorized
import Unauthorized from "./components/common/unauthorized";

//Pre-Landing
import PreLanding from "./components/common/pre-landing";

//Order
import OrderList from "./components/order/order-list.js";
import OrderCreate from "./components/order/order-create.jsx";
import OrderView from "./components/order/order-view.js";

//Invoice
import InvoiceCreate from "./components/invoice/invoice-create.jsx";
import InvoiceView from "./components/invoice/invoice-view.jsx";
import InvoiceList from "./components/invoice/invoice-list.js";

//Custom Modules
import ModuleBuilder from "./components/custom-module/module-builder.js";
import CustomRecordList from "./components/custom-module/record-list.js";
import CustomRecordCreate from "./components/custom-module/record-create.js";
import CustomRecordView from "./components/custom-module/record-view.js";
import { CUSTOM_MODULES, MODULE_BUILDER } from "./components/custom-module/custom-module-api.js";

function App() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const accountsUrl = process.env.REACT_APP_ACCOUNTS_URL;
  const environment = process.env.REACT_APP_ENVIRONMENT;
  const { rolePermissionList, setRolePermissionList, setUserType, setOrgImage } = useGlobal();

  const [loading, setLoading] = useState(true);

  // 🔒 Check token validity on load
  useEffect(() => {
    const checkToken = async () => {
      try {
        console.log("🔍 Checking token validity...");

        const response = await apiCallAdmin("GET", "validatetoken");

        console.log("✅ Token status:", response.status);

        if (response.status === 401) {
          handleInvalidToken();
        }
      } catch (err) {
        console.error("❌ Token validation failed:", err);
        handleInvalidToken();
      }
    };

    const handleInvalidToken = () => {
      const currentURL = window.location.href;

      const redirectData = {
        redirectPath: currentURL,
        app: "crm",
        ts: Date.now(),
      };

      console.warn("🔐 Saving redirect in window.name:", redirectData);
      window.name = JSON.stringify(redirectData);
      console.log("📌 window.name set:", window.name);

      document.cookie =
        "mawalog=; path=/; domain=.mawalog.com; expires=Thu, 01 Jan 1970 00:00:00 GMT";

      console.log("➡️ Redirecting to login:", `${accountsUrl}/login`);
      // window.location.href = `${accountsUrl}/login`;
    };

    console.log("🚀 Initial token check");
    checkToken();

    const onFocus = () => {
      console.log("👁 Window focused → rechecking token");
      checkToken();
    };

    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // 🔒 Fetch role permissions on load
  useEffect(() => {
    getRolePermissions();
  }, []);

  // 📡 Broadcast channel for sync
  useEffect(() => {
    const channel = new BroadcastChannel(`auth_channel_mawalog_${environment}`);
    channel.onmessage = () => {
      window.location.reload();
    };
    return () => channel.close();
  }, []);

  // 🔐 Get role permissions from API
  const getRolePermissions = async () => {
    try {
      console.log("[DEBUG] Starting getRolePermissions...");

      // Using application 24 (from ticketing-web reference), can be made dynamic
      const response = await apiCallAdmin("GET", "get_role_permission/24");

      if (response.status === 200 && response.data.success) {
        const rolePermissions = response.data.role_permission ?? [];
        const userType = response.data.user_type ?? "";

        setRolePermissionList(rolePermissions);
        setUserType(userType);

        localStorage.setItem("rolePermissionList", JSON.stringify(rolePermissions));
        localStorage.setItem("userType", userType);

        console.log("[DEBUG] Role permissions set:", rolePermissions);
        console.log("[DEBUG] User type set:", userType);
      } else {
        console.log("[DEBUG] API returned success=false");
        setRolePermissionList([]);
        setUserType("");
      }
    } catch (err) {
      console.error("[DEBUG] Error fetching role permissions:", err);
      setRolePermissionList([]);
      setUserType("");
    } finally {
      setLoading(false);
      console.log("[DEBUG] getRolePermissions finished");
    }
  };

  // 🔍 Check if user has permission for a module and action
  const checkPermission = (moduleName, permission) => {
    const hasPermission = rolePermissionList.findIndex(
      (element) =>
        element["name"] === moduleName &&
        element["menu_permission"] === true &&
        element[permission] === true
    ) > -1;

    console.log(`[DEBUG] Permission check - Module: ${moduleName}, Permission: ${permission}, Allowed: ${hasPermission}`);
    return hasPermission;
  };

  // 🛡️ Protected route wrapper
  const ProtectedRoute = ({ isAllowed }) => {
    return isAllowed ? <Outlet /> : <Navigate to="/unauthorized" />;
  };

  if (loading) {
    return <Loader height="100vh" />;
  }

  // Component to handle layout based on route
  const AppLayout = () => {
    const location = useLocation();
    // Sidebar collapse state lives here (not in App) so toggling re-renders
    // only this layout — not App — which keeps AppLayout/ProtectedRoute
    // identities stable and prevents the routed page from remounting (and
    // re-firing its mount-time API calls).
    const { isSidebarCollapsed } = useUI();
    const sidebarWidth = isSidebarCollapsed ? "72px" : "200px";

    // Check if current route is unauthorized page or pre-landing page
    const isUnauthorizedPage = location.pathname === "/unauthorized";
    const isPreLandingPage = location.pathname === "/pre-landing";
    const hideNavigation = isUnauthorizedPage || isPreLandingPage;

    // These list pages mount their own module chat panel. Both are
    // position:fixed at the same spot, so showing the global one too puts two
    // buttons on top of each other - the module's own panel wins on its own
    // menu, because it is the more focused of the two.
    const MODULE_CHAT_PAGES = [
      "/leads/list",
      "/accounts/list",
      "/quotation/list",
      "/order/list",
      "/invoice/list",
    ];
    const hasModuleChat = MODULE_CHAT_PAGES.includes(location.pathname);

    return (
      <>
        {!hideNavigation && <CallReminderNotifier />}
        {!hideNavigation && <Topbar />}

        <div
          style={{
            display: "flex",
            width: "100%",
            height: hideNavigation ? "100vh" : "calc(-64px + 100vh)",
          }}
        >
          {/* Sidebar - Hidden on unauthorized and pre-landing pages */}
          {!hideNavigation && (
            <div
              style={{
                width: sidebarWidth,
                height: "100%",
                borderRight: "0.6px solid #EBEBEB",
                transition: "width 0.25s ease",
              }}
            >
              <Sidebar />
            </div>
          )}

          {/* Main content */}
          <div
            style={{
              width: hideNavigation ? "100%" : `calc(100% - ${sidebarWidth})`,
              height: "100%",
              overflowY: "auto",
              backgroundColor: "#f8f9fb",
              transition: "width 0.25s ease",
            }}
          >
          <Routes>
             {/* Pre-Landing */}
            <Route path="/" element={<PreLanding />} />
            {/* Dashboard - No permission check needed */}
             <Route
              element={<ProtectedRoute isAllowed={checkPermission("Dashboard", "list_permission")} />}
            >
              <Route path="/dashboard" element={<Dashboard />} /> 
            </Route>
           
            <Route path="/revenue" element={<RevenueDashboard />} />
            <Route path="/salesperson" element={<SalespersonDashboard />} />
            <Route path="/pipeline" element={<PipelineDashboard />} />
            <Route path="/forecast" element={<ForecastDashboard />} />
            <Route path="/next-action" element={<NextActionDashboard />} />

            {/* Leads - Protected Routes */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Leads", "list_permission")} />}
            >
              <Route path="/leads/list" element={<LeadsList />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Leads", "create_permission")} />}
            >
              <Route path="/leads/create" element={<LeadsAdd />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Leads", "view_permission")} />}
            >
              <Route path="/leads/view" element={<LeadsView />} />
            </Route>

            {/* Contacts - Protected Routes */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Contacts", "list_permission")} />}
            >
              <Route path="/contacts/list" element={<ContactsList />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Contacts", "create_permission")} />}
            >
              <Route path="/contacts/create" element={<ContactsCreate />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Contacts", "view_permission")} />}
            >
              <Route path="/contacts/view" element={<ContactsView />} />
            </Route>

            {/* Accounts - Protected Routes */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Accounts", "list_permission")} />}
            >
              <Route path="/accounts/list" element={<AccountList />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Accounts", "create_permission")} />}
            >
              <Route path="/accounts/create" element={<AccountCreate />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Accounts", "view_permission")} />}
            >
              <Route path="/accounts/view" element={<AccountView />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Accounts", "list_permission")} />}
            >
              <Route path="/accounts/outstanding-schedules" element={<OutstandingSchedulesList />} />
            </Route>

            {/* Opportunity - Protected Routes */}
           {/*  <Route
              element={<ProtectedRoute isAllowed={checkPermission("Opportunity", "list_permission")} />}
            > */}
              <Route path="/opportunity/list" element={<OpportunityList />} />
           {/*  </Route> */}
           {/*  <Route
              element={<ProtectedRoute isAllowed={checkPermission("Opportunity", "create_permission")} />}
            > */}
              <Route path="/opportunity/create" element={<OpportunityCreate />} />
            {/* </Route> */}
            {/* <Route
              element={<ProtectedRoute isAllowed={checkPermission("Opportunity", "view_permission")} />}
            > */}
              <Route path="/opportunity/view" element={<OpportunityView />} />
            {/* </Route> */}

            {/* Item - Protected Routes */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Item", "list_permission")} />}
            >
              <Route path="/item/list" element={<ItemList />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Item", "create_permission")} />}
            >
              <Route path="/item/create" element={<ItemCreate />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Item", "view_permission")} />}
            >
              <Route path="/item/view" element={<ItemView />} />
            </Route>

            {/* Quotations - Protected Routes */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Quotation", "list_permission")} />}
            >
              <Route path="/quotation/list" element={<QuotationList />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Quotation", "create_permission")} />}
            >
              <Route path="/quotation/create" element={<QuotationCreate />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Quotation", "view_permission")} />}
            >
              <Route path="/quotation/view/:id" element={<QuotationView />} />
            </Route>

            {/* Orders - Protected Routes */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Order", "list_permission")} />}
            >
              <Route path="/order/list" element={<OrderList />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Order", "create_permission")} />}
            >
              <Route path="/order/create" element={<OrderCreate />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Order", "view_permission")} />}
            >
              <Route path="/order/view" element={<OrderView />} />
            </Route>

            {/* Invoices - Protected Routes */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Invoice", "list_permission")} />}
            >
              <Route path="/invoice/list" element={<InvoiceList />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Invoice", "create_permission")} />}
            >
              <Route path="/invoice/create" element={<InvoiceCreate />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Invoice", "view_permission")} />}
            >
              <Route path="/invoice/view" element={<InvoiceView />} />
            </Route>

            {/* Calls - Protected Routes */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Calls", "list_permission")} />}
            >
              <Route path="/calls/list" element={<CallsList />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Calls", "create_permission")} />}
            >
              <Route path="/calls/create" element={<CallsAdd />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Calls", "view_permission")} />}
            >
              <Route path="/calls/view" element={<CallsView />} />
            </Route>

            {/* Module Builder & Custom Modules - every module built in the
                builder shares the "Custom Modules" permissions. */}
           {/*  <Route
              element={<ProtectedRoute isAllowed={checkPermission(MODULE_BUILDER, "list_permission")} />}
            > */}
              <Route path="/module-builder/list" element={<ModuleBuilder />} />
           {/*  </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission(CUSTOM_MODULES, "list_permission")} />}
            > */}
              <Route path="/custom/:slug/list" element={<CustomRecordList />} />
           {/*  </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission(CUSTOM_MODULES, "create_permission")} />}
            > */}
              <Route path="/custom/:slug/create" element={<CustomRecordCreate />} />
           {/*  </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission(CUSTOM_MODULES, "edit_permission")} />}
            > */}
              <Route path="/custom/:slug/edit/:id" element={<CustomRecordCreate />} />
            {/* </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission(CUSTOM_MODULES, "view_permission")} />}
            > */}
              <Route path="/custom/:slug/view/:id" element={<CustomRecordView />} />
           {/*  </Route> */}

            {/* Calendar */}
            <Route path="/calendar" element={<CalendarView />} />

            {/* Announcements - Protected Routes */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Announcement", "list_permission")} />}
            >
              <Route path="/announcement/list" element={<AnnouncementList />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Announcement", "create_permission")} />}
            >
              <Route path="/announcement/add" element={<AnnouncementAdd />} />
            </Route>

            {/* Payment - Protected Routes */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Payment", "list_permission")} />}
            >
              <Route path="/payment/list" element={<PaymentListing />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Payment", "create_permission")} />}
            >
              <Route path="/payment/create" element={<PaymentCreate />} />
            </Route>
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Payment", "view_permission")} />}
            >
              <Route path="/payment/view/:id" element={<PaymentView />} />
              <Route path="/payment/history/:id" element={<PaymentHistoryView />} />
            </Route>

            {/* Report - Protected Route */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Report", "menu_permission")} />}
            >
              <Route path="/report" element={<ReportPage />} />
            </Route>

            {/* AI usage shows org-wide spend, so it follows the AI chat
                permission rather than riding on Report's. */}
            <Route
              element={<ProtectedRoute isAllowed={checkPermission("Ask MAI", "menu_permission")} />}
            >
              <Route path="/ai-usage" element={<AiUsage />} />
            </Route>

            {/* Help */}
            <Route path="/help" element={<HelpQuestions />} />

           

            {/* Unauthorized */}
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
            </div>

            {/* Cross-module chatbot. Inside the authenticated shell, so it is
                absent from the login and pre-landing screens, and stood down
                on menus that carry their own module chat. */}
            {!hideNavigation  && (
              <AiChat
                askApi={askCrmChatApi}
                providersApi={getCrmAiProvidersApi}
                title="Ask MAI"
                hint="Ask about leads, accounts, quotations, orders or invoices — “how many invoices are unpaid?”, “export leads from the last 6 months”."
              />
            )}
            {!hideNavigation && <V1MigrationModal />}
          </div>
        </>
      );
    };

  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
}

export default App;
